import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrencySimple } from './currency';
import { DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';

const formatStatus = (status?: string) => (status || 'pending').replace('_', ' ').toUpperCase();

const formatDate = (value?: string) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const getReceiptNumber = (payment: any) => {
  if (payment?.receipt_number) return payment.receipt_number;
  const datePart = String(payment?.payment_date || '').replace(/-/g, '') || 'DRAFT';
  return `SOMS-PAYOUT-${datePart}-${String(payment?.id || '0000').padStart(5, '0')}`;
};

export const downloadStaffPaymentReceiptPdf = (payment: any, context: any = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const receiptNumber = getReceiptNumber(payment);
  const currencyCode = payment?.currency_code || context.currencyCode || DEFAULT_CURRENCY_CODE;
  const studioName = payment?.studio_name || context.studioName || 'SOMS Studio';
  const recipientName = payment?.recipient_full_name || payment?.recipient_name || context.recipientName || 'Team member';
  const remainingBalance = context.remainingBalance ?? payment?.remaining_balance;
  const paidToDate = context.paidToDate ?? payment?.paid_to_date;
  const generatedAt = new Date();

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, 210, 42, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text('SOMS Studio Operations', 14, 17);
  doc.setFontSize(11);
  doc.text('Producer & Staff Payment Receipt', 14, 27);
  doc.setTextColor(203, 213, 225);
  doc.setFontSize(9);
  doc.text(`Generated ${generatedAt.toLocaleString()}`, 14, 34);

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(14, 52, 182, 24, 3, 3, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(11);
  doc.text(`Receipt No: ${receiptNumber}`, 18, 61);
  doc.setTextColor(71, 85, 105);
  doc.setFontSize(10);
  doc.text(`Studio: ${studioName}`, 18, 69);

  autoTable(doc, {
    startY: 86,
    head: [['Payment Detail', 'Value']],
    body: [
      ['Paid To', recipientName],
      ['Amount Paid', formatCurrencySimple(payment?.amount || 0, currencyCode)],
      ['Payment Date', formatDate(payment?.payment_date)],
      ['Month / Period', payment?.month_year || 'Current period'],
      ['Status', formatStatus(payment?.status)],
      ['Notes', payment?.notes || 'No notes recorded'],
      ['Paid To Date', paidToDate !== undefined ? formatCurrencySimple(paidToDate, currencyCode) : 'Calculated in dashboard'],
      ['Remaining Balance', remainingBalance !== undefined ? formatCurrencySimple(Math.max(0, Number(remainingBalance || 0)), currencyCode) : 'Calculated in dashboard'],
    ],
    styles: {
      fontSize: 10,
      cellPadding: 3,
      lineColor: [226, 232, 240],
    },
    columnStyles: {
      0: { cellWidth: 56, fontStyle: 'bold', fillColor: [241, 245, 249] },
      1: { cellWidth: 118 },
    },
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    margin: { left: 14, right: 14 },
    theme: 'grid',
  });

  const endY = (doc as any).lastAutoTable?.finalY || 136;
  doc.setDrawColor(226, 232, 240);
  doc.line(14, endY + 14, 196, endY + 14);
  doc.setTextColor(71, 85, 105);
  doc.setFontSize(9);
  doc.text('This receipt was generated automatically by SOMS and does not require an uploaded proof document.', 14, endY + 24);
  doc.text('Use it for payout confirmation, reconciliation, and internal studio records.', 14, endY + 30);

  doc.save(`${receiptNumber}.pdf`);
};
